import { safeStorage } from "@/shared/lib/safeStorage";

// Merchant IDs
export const MERCHANT_ID_MBATPA = "MBATPA";
export const MERCHANT_ID_BHPI = "BHPI";
export const MERCHANT_ID_PGLX = "PGLX";

// Client IDs
export const PAYER_CLIENT_PLACEHOLDER_ID = 1273; // MBATPA
export const BHPI_CLIENT_PLACEHOLDER_ID = 1278; // BHPI

// Placeholder names
export const PAYER_CLIENT_PLACEHOLDER_NAME = "Client 1273";
export const BHPI_CLIENT_PLACEHOLDER_NAME = "Client 1278";

// Placeholder objects
export const PAYER_CLIENT_PLACEHOLDER = {
  id: PAYER_CLIENT_PLACEHOLDER_ID,
  name: PAYER_CLIENT_PLACEHOLDER_NAME,
} as const;

export const BHPI_CLIENT_PLACEHOLDER = {
  id: BHPI_CLIENT_PLACEHOLDER_ID,
  name: BHPI_CLIENT_PLACEHOLDER_NAME,
} as const;

/**
 * Get the client ID associated with a merchant ID
 * @param merchantId - The merchant ID (e.g., "MBATPA", "BHPI")
 * @returns The client ID (1273 for MBATPA, 1278 for BHPI) or null if not found
 */
export const getMerchantClientId = (merchantId: string | undefined): number | null => {
  if (!merchantId) return null;

  switch (merchantId.toUpperCase()) {
    case MERCHANT_ID_MBATPA:
      return PAYER_CLIENT_PLACEHOLDER_ID;
    case MERCHANT_ID_BHPI:
      return BHPI_CLIENT_PLACEHOLDER_ID;
    case MERCHANT_ID_PGLX:
      return PAYER_CLIENT_PLACEHOLDER_ID; // 1273, used for source-filter APIs when testing as hierarchy
    default:
      return null;
  }
};

/**
 * Get the merchant ID associated with a client ID (reverse of getMerchantClientId).
 * Used when deriving merchantId from the selected Client filter in User Management.
 * @param clientId - The client ID (e.g., 1273, 1278)
 * @returns The merchant ID (e.g., "MBATPA", "BHPI") or null if not found
 */
export const getMerchantIdFromClientId = (clientId: number | undefined | null): string | null => {
  if (clientId == null) return null;
  if (clientId === PAYER_CLIENT_PLACEHOLDER_ID) return MERCHANT_ID_MBATPA;
  if (clientId === BHPI_CLIENT_PLACEHOLDER_ID) return MERCHANT_ID_BHPI;
  return null;
};

/**
 * Check if the client filter should be disabled based on source and merchant ID
 * @param source - The user's source (e.g., "payer")
 * @param merchantId - The merchant ID
 * @returns true if the client filter should be disabled
 */
export const shouldDisableClientFilter = (
  source: string | undefined,
  _merchantId: string | undefined
): boolean => {
  // Disable if source is "payer" (covers both MBATPA and BHPI)
  return source === "payer";
};

/**
 * Get the appropriate client placeholder based on merchant ID
 * @param merchantId - Optional merchant ID to determine which placeholder to return
 * @returns The appropriate placeholder object
 */
export const getPayerClientPlaceholder = (merchantId?: string) => {
  if (merchantId?.toUpperCase() === MERCHANT_ID_BHPI) {
    return BHPI_CLIENT_PLACEHOLDER;
  }
  // Default to MBATPA placeholder for backward compatibility
  return PAYER_CLIENT_PLACEHOLDER;
};

// localStorage key for storing payer merchantId
const PAYER_MERCHANT_ID_KEY = "payerMerchantId";

/**
 * Get merchantId from localStorage (fallback for page reload)
 * @returns The merchantId if found, null otherwise
 */
export const getStoredPayerMerchantId = (): string | null => {
  // safeStorage is SSR-safe and swallows access errors (e.g. incognito mode).
  return safeStorage.getItem(PAYER_MERCHANT_ID_KEY);
};

/**
 * Clear stored payer merchantId from localStorage
 */
export const clearStoredPayerMerchantId = (): void => {
  safeStorage.removeItem(PAYER_MERCHANT_ID_KEY);
};

/**
 * Get merchantId with fallback: Redux -> localStorage -> null
 * @param merchantIdFromRedux - MerchantId from Redux state
 * @returns The merchantId from Redux or localStorage fallback
 */
export const getMerchantIdWithFallback = (
  merchantIdFromRedux: string | undefined
): string | null => {
  if (merchantIdFromRedux) {
    return merchantIdFromRedux;
  }
  return getStoredPayerMerchantId();
};
